import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { capitalizeFirstLetter } from "../../../utils/letters";
import { formatDate } from "../../../utils/date";
import { listCompanyContracts } from "../../../api/company";

const CONTRACT_ROWS_PER_PAGE_DEFAULT = 10;

const CONTRACT_SORT_OPTIONS = [
	{ value: "code", label: "Código" },
	{ value: "name", label: "Nombre" },
	{ value: "start_date", label: "Inicio" },
	{ value: "end_date", label: "Término" },
	{ value: "quota", label: "Cupo" },
	{ value: "status", label: "Estado" },
	{ value: "reason", label: "Motivo" },
];

const CONTRACT_ALLOWED_ROWS = [10, 25, 50, 100];
const CONTRACT_ALLOWED_SORTS = new Set(
	CONTRACT_SORT_OPTIONS.map((option) => option.value),
);
const CONTRACT_ALLOWED_DIRECTIONS = new Set(["asc", "desc"]);
const CONTRACT_ALLOWED_FILTERS = new Set(["all", "available", "unavailable"]);

function parsePositiveInt(rawValue, fallbackValue) {
	const parsed = Number(rawValue);
	if (!Number.isInteger(parsed) || parsed < 1) return fallbackValue;
	return parsed;
}

function parseRows(rawValue) {
	const parsed = Number(rawValue);
	if (!CONTRACT_ALLOWED_ROWS.includes(parsed)) {
		return CONTRACT_ROWS_PER_PAGE_DEFAULT;
	}
	return parsed;
}

function parseSort(rawValue) {
	if (rawValue === "validity") return "start_date";
	if (!CONTRACT_ALLOWED_SORTS.has(rawValue)) return "start_date";
	return rawValue;
}

function parseDirection(rawValue) {
	if (!CONTRACT_ALLOWED_DIRECTIONS.has(rawValue)) return "desc";
	return rawValue;
}

function parseStatusFilter(rawValue) {
	if (!CONTRACT_ALLOWED_FILTERS.has(rawValue)) return "all";
	return rawValue;
}

function getContractStatusText(contract) {
	return contract?.can_enroll ? "Disponible" : "No disponible";
}

function getContractReasonText(contract) {
	if (contract?.can_enroll) return "-";

	const reasons = [];
	if (Number(contract?.remaining_slots || 0) <= 0) reasons.push("Cupo lleno");
	if (!contract?.is_active) reasons.push("Curso deshabilitado");
	const runStatus = (contract?.run_status || "").toLowerCase();
	if (runStatus === "finished") reasons.push("Curso finalizado");
	if (runStatus === "closed") reasons.push("Curso cerrado");

	return reasons.length > 0 ? reasons.join(" / ") : "-";
}

function escapeRegExp(value) {
	return value.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

function highlightSearchMatch(value, query) {
	const text = (value ?? "").toString();
	const needle = query.trim();
	if (!needle) return text;

	const matcher = new RegExp(`(${escapeRegExp(needle)})`, "ig");
	const parts = text.split(matcher);
	let cursor = 0;

	return parts.map((part) => {
		const key = `${cursor}-${part}`;
		cursor += part.length;
		if (part.toLowerCase() === needle.toLowerCase()) {
			return (
				<mark className="search-highlight" key={`hit-${key}`}>
					{part}
				</mark>
			);
		}
		return <span key={`txt-${key}`}>{part}</span>;
	});
}

export default function CompanyDashboard() {
	const [searchParams, setSearchParams] = useSearchParams();
	const [search, setSearch] = useState(() => searchParams.get("q") || "");
	const [contracts, setContracts] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [rowsPerPage, setRowsPerPage] = useState(() =>
		parseRows(searchParams.get("rows")),
	);
	const [currentPage, setCurrentPage] = useState(() =>
		parsePositiveInt(searchParams.get("page"), 1),
	);
	const [sortField, setSortField] = useState(() =>
		parseSort(searchParams.get("sort")),
	);
	const [sortDirection, setSortDirection] = useState(() =>
		parseDirection(searchParams.get("dir")),
	);
	const [statusFilter, setStatusFilter] = useState(() =>
		parseStatusFilter(searchParams.get("status")),
	);

	function resetTableState() {
		setSearch("");
		setRowsPerPage(CONTRACT_ROWS_PER_PAGE_DEFAULT);
		setCurrentPage(1);
		setSortField("start_date");
		setSortDirection("desc");
		setStatusFilter("all");
		setSearchParams(new URLSearchParams(), { replace: true });
	}


	async function refresh() {
		setLoading(true);
		setError(null);
		try {
			const data = await listCompanyContracts();
			setContracts(Array.isArray(data) ? data : []);
		} catch (e) {
			const status = e?.response?.status;
			const msg =
				status === 403
					? "No tienes permiso para esta acción. Cierra sesión y entra de nuevo como representante de organización."
					: e?.response?.data?.error ||
					e?.response?.data?.detail ||
					e?.response?.data?.message ||
					(e?.message && typeof e.message === "string" ? e.message : null) ||
					"No se pudo cargar la información de contratos.";
			setError(typeof msg === "string" ? msg : JSON.stringify(msg));
		} finally {
			setLoading(false);
		}
	}
	useEffect(() => {
		refresh();
	}, []);

	useEffect(() => {
		const nextSearch = searchParams.get("q") || "";
		const nextRows = parseRows(searchParams.get("rows"));
		const nextPage = parsePositiveInt(searchParams.get("page"), 1);
		const nextSort = parseSort(searchParams.get("sort"));
		const nextDirection = parseDirection(searchParams.get("dir"));
		const nextStatus = parseStatusFilter(searchParams.get("status"));

		setSearch((prev) => (prev === nextSearch ? prev : nextSearch));
		setRowsPerPage((prev) => (prev === nextRows ? prev : nextRows));
		setCurrentPage((prev) => (prev === nextPage ? prev : nextPage));
		setSortField((prev) => (prev === nextSort ? prev : nextSort));
		setSortDirection((prev) =>
			prev === nextDirection ? prev : nextDirection,
		);
		setStatusFilter((prev) => (prev === nextStatus ? prev : nextStatus));
	}, [searchParams]);

	const filteredContracts = useMemo(() => {
		const q = search.trim().toLowerCase();
		return contracts.filter((c) => {
			const isAvailable = Boolean(c.can_enroll);
			if (statusFilter === "available" && !isAvailable) return false;
			if (statusFilter === "unavailable" && isAvailable) return false;

			if (!q) return true;

			const nombreStr = String((c.course_name || "")).toLowerCase();
			const codigoStr = String((c.course_code || "")).toLowerCase();
			const fechasStr = String(`${formatDate(c.contract_start_date)} ${formatDate(c.contract_end_date)}`).toLowerCase();
			const cupoStr = `${c.current_enrollments} ${c.max_students} ${c.remaining_slots}`;
			const estadoStr = getContractStatusText(c).toLowerCase();
			const motivoStr = getContractReasonText(c).toLowerCase();
			return (
				codigoStr.includes(q) ||
				nombreStr.includes(q) ||
				fechasStr.includes(q) ||
				cupoStr.includes(q) ||
				estadoStr.includes(q) ||
				motivoStr.includes(q)
			);
		});
	}, [contracts, search, statusFilter]);

	const sortedContracts = useMemo(() => {
		const data = [...filteredContracts];

		data.sort((left, right) => {
			let leftValue;
			let rightValue;

			switch (sortField) {
				case "code":
					leftValue = (left.course_code || "").toLowerCase();
					rightValue = (right.course_code || "").toLowerCase();
					break;
				case "name":
					leftValue = (left.course_name || "").toLowerCase();
					rightValue = (right.course_name || "").toLowerCase();
					break;
				case "quota":
					leftValue = Number(left.remaining_slots || 0);
					rightValue = Number(right.remaining_slots || 0);
					break;
				case "status":
					leftValue = left.can_enroll ? 1 : 0;
					rightValue = right.can_enroll ? 1 : 0;
					break;
				case "reason":
					leftValue = getContractReasonText(left).toLowerCase();
					rightValue = getContractReasonText(right).toLowerCase();
					break;
				case "end_date":
					leftValue = new Date(left.contract_end_date || 0).getTime();
					rightValue = new Date(right.contract_end_date || 0).getTime();
					break;
				case "start_date":
				default:
					leftValue = new Date(left.contract_start_date || 0).getTime();
					rightValue = new Date(right.contract_start_date || 0).getTime();
					break;
			}

			if (leftValue === rightValue) return 0;
			if (sortDirection === "asc") return leftValue > rightValue ? 1 : -1;
			return leftValue < rightValue ? 1 : -1;
		});

		return data;
	}, [filteredContracts, sortField, sortDirection]);

	useEffect(() => {
		setCurrentPage(1);
	}, [search, statusFilter, rowsPerPage, sortField, sortDirection]);

	const totalRows = sortedContracts.length;
	const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
	const safeCurrentPage = Math.min(currentPage, totalPages);
	const startIndex = totalRows === 0 ? 0 : (safeCurrentPage - 1) * rowsPerPage;
	const endIndex = Math.min(startIndex + rowsPerPage, totalRows);
	const contractsToShow = sortedContracts.slice(startIndex, startIndex + rowsPerPage);

	useEffect(() => {
		if (currentPage > totalPages) {
			setCurrentPage(totalPages);
		}
	}, [currentPage, totalPages]);

	useEffect(() => {
		const nextParams = new URLSearchParams(searchParams);

		if (search.trim()) nextParams.set("q", search.trim());
		else nextParams.delete("q");

		if (rowsPerPage !== CONTRACT_ROWS_PER_PAGE_DEFAULT) {
			nextParams.set("rows", String(rowsPerPage));
		} else {
			nextParams.delete("rows");
		}

		if (safeCurrentPage !== 1) nextParams.set("page", String(safeCurrentPage));
		else nextParams.delete("page");

		if (sortField !== "start_date") nextParams.set("sort", sortField);
		else nextParams.delete("sort");

		if (sortDirection !== "desc") nextParams.set("dir", sortDirection);
		else nextParams.delete("dir");

		if (statusFilter !== "all") nextParams.set("status", statusFilter);
		else nextParams.delete("status");

		if (nextParams.toString() !== searchParams.toString()) {
			setSearchParams(nextParams, { replace: true });
		}
	}, [
		search,
		rowsPerPage,
		safeCurrentPage,
		sortField,
		sortDirection,
		statusFilter,
		searchParams,
		setSearchParams,
	]);

	return (
		<>
			<div className="p-4 backoffice-tables flex-grow-1 overflow-auto font-n">
				<div className="container-principal">
					<div className="w-100">
						<h2 className="m-0">Inicio</h2>
						<div className="admin-subtittle">
							<span>Cursos / Contratos de tu organización</span>
						</div>
					</div>
				</div>

				{loading && <p>Cargando...</p>}
				{error && (
					<div className="alert alert-danger portal-emp-alert" role="alert">
						<i className="ri-error-warning-line me-2" aria-hidden />
						{error}
					</div>
				)}

				{!loading && !error && contracts.length === 0 && (
					<p>No hay contratos asociados a tu organización.</p>
				)}

				<div className="card mt-3 shadow-sm">
					<div className="card-body p-0">
						<div className="table-toolbar table-toolbar-locked">
							<div className="toolbar-group toolbar-pagination-group">
								<button
									type="button"
									className="toolbar-nav-btn"
									onClick={() => setCurrentPage(1)}
									disabled={safeCurrentPage === 1}
								>
									&lt;&lt;
								</button>
								<button
									type="button"
									className="toolbar-nav-btn"
									onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
									disabled={safeCurrentPage === 1}
								>
									&lt;
								</button>
								<select
									className="form-select toolbar-select toolbar-page-select"
									value={safeCurrentPage}
									onChange={(e) => setCurrentPage(Number(e.target.value))}
									disabled={totalPages <= 1}
								>
									{Array.from({ length: totalPages }, (_, idx) => idx + 1).map((page) => (
										<option key={page} value={page}>
											{page}
										</option>
									))}
								</select>
								<button
									type="button"
									className="toolbar-nav-btn"
									onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
									disabled={safeCurrentPage >= totalPages}
								>
									&gt;
								</button>
								<button
									type="button"
									className="toolbar-nav-btn"
									onClick={() => setCurrentPage(totalPages)}
									disabled={safeCurrentPage >= totalPages}
								>
									&gt;&gt;
								</button>
							</div>

							<div className="search-group toolbar-search-group d-flex align-items-center gap-2">
								<input
									className="form-control"
									value={search}
									onChange={(e) => setSearch(e.target.value)}
									placeholder="Buscar por código, fecha o estado..."
									maxLength={120}
								/>
								<button
									type="button"
									className="toolbar-nav-btn"
									onClick={() => setSearch("")}
									disabled={!search.trim()}
									title="Limpiar búsqueda"
									aria-label="Limpiar búsqueda"
								>
									<i className="ri-eraser-line" />
								</button>
							</div>

							<div className="toolbar-group toolbar-controls-group">
								<label className="d-flex align-items-center gap-2 mb-0">
									<span>Número de filas:</span>
									<select
										className="form-select toolbar-select"
										value={rowsPerPage}
										onChange={(e) => setRowsPerPage(Number(e.target.value))}
									>
										<option value={10}>10</option>
										<option value={25}>25</option>
										<option value={50}>50</option>
										<option value={100}>100</option>
									</select>
								</label>

								<label className="d-flex align-items-center gap-2 mb-0">
									<span>Filtro:</span>
									<select
										className="form-select toolbar-select"
										value={statusFilter}
										onChange={(e) => setStatusFilter(e.target.value)}
									>
										<option value="all">Todos</option>
										<option value="available">Disponibles</option>
										<option value="unavailable">No disponibles</option>
									</select>
								</label>

								<label className="d-flex align-items-center gap-2 mb-0">
									<span>Ordenar por:</span>
									<select
										className="form-select toolbar-select"
										value={sortField}
										onChange={(e) => setSortField(e.target.value)}
									>
										{CONTRACT_SORT_OPTIONS.map((option) => (
											<option key={option.value} value={option.value}>
												{option.label}
											</option>
										))}
									</select>
								</label>

								<button
									type="button"
									className="toolbar-order-btn"
									onClick={() => setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))}
								>
									{sortDirection === "asc" ? "Asc" : "Desc"}
								</button>

								<button
									type="button"
									className="toolbar-nav-btn"
									onClick={resetTableState}
									title="Reiniciar tabla"
								>
									Reiniciar
								</button>
							</div>
						</div>

						<div className="table-responsive table-wrapper-scroll">
							<table className="table table-hover mb-0 align-middle">
								<thead className="table-light">
									<tr>
										<th style={{ width: 90 }}><span>Código</span></th>
										<th style={{ width: 160 }}><span>Nombre</span></th>
										<th style={{ width: 100 }}><span>Inicio</span></th>
										<th style={{ width: 100 }}><span>Término</span></th>
										<th style={{ width: 90 }}><span>Cupo</span></th>
										<th style={{ width: 140 }}><span>Estado</span></th>
										<th style={{ width: 180 }}><span>Motivo</span></th>
									</tr>
								</thead>
								<tbody>
									{loading ? (
										<tr>
											<td colSpan="7" className="text-muted p-4">
												<span>Cargando...</span>
											</td>
										</tr>
									) : contractsToShow.length === 0 ? (
										<tr>
											<td colSpan="7" className="text-muted p-4">
												<span>Sin resultados.</span>
											</td>
										</tr>
									) : (
										contractsToShow.map((c) => (
											<tr key={c.id}>
												<td><span>{highlightSearchMatch(c.course_code || "-", search)}</span></td>
												<td><span>{highlightSearchMatch(capitalizeFirstLetter(c.course_name), search)}</span></td>
												<td>
													<span>{highlightSearchMatch(`${formatDate(c.contract_start_date)}`, search)}</span>
												</td>
												<td>
													<span>{highlightSearchMatch(`${formatDate(c.contract_end_date)}`, search)}</span>
												</td>
												<td>
													<span>{highlightSearchMatch(`${c.current_enrollments}/${c.max_students}`, search)}</span>
												</td>
												<td>
													<span>{highlightSearchMatch(getContractStatusText(c), search)}</span>
												</td>
												<td>
													<span>{highlightSearchMatch(getContractReasonText(c), search)}</span>
												</td>
											</tr>
										))
									)}
								</tbody>
							</table>
						</div>
						<div className="table-footer-summary">
							<span>
								Mostrando {totalRows === 0 ? 0 : startIndex + 1}-{endIndex} de {totalRows} contratos
							</span>
						</div>
					</div>
				</div>
				<div className="description-txt">
					<p className="mb-2"><strong>Disponible:</strong> El curso cuenta con cupos libres para cargar y matricular estudiantes. Puedes realizar preinscripciones incluso antes de que comience el periodo. La capacidad se evalúa de manera individual, por lo que si un curso se llena, no afectará la disponibilidad de los demás.</p>
					<hr />
					<p className="mb-2"><strong>No disponible:</strong> El curso ha alcanzado su límite máximo de capacidad (ej. 30/30) y no admite nuevas matrículas. </p>
				</div>
			</div>
		</>
	);
}
